import { useState, useEffect, useRef } from "react";
import AgentService from "../../services/api/agents";
import WorkflowService from "../../services/api/workflow";

/** Set to true to re-enable Service execution mode. */
const SERVICE_MODE_ENABLED = false;

const MODE_OPTIONS = [
  {
    value: "orch",
    label: "Orchestrator",
    icon: "fa-diagram-project",
  },
  {
    value: "svc",
    label: "Service",
    icon: "fa-plug",
    disabled: !SERVICE_MODE_ENABLED,
  },
];

function ModeSegment({ value, onChange, disabled }) {
  return (
    <div className="orch-mode-segment" role="radiogroup" aria-label="Execution mode">
      {MODE_OPTIONS.map((opt) => {
        const active = value === opt.value;
        const isDisabled = disabled || Boolean(opt.disabled);
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`orch-mode-segment-btn${active ? " orch-mode-segment-btn--active" : ""}`}
            onClick={() => onChange(opt.value)}
            disabled={isDisabled}
            title={opt.disabled ? "Service mode is currently unavailable" : undefined}
          >
            <span className="orch-mode-segment-icon">
              <i className={`fa-solid ${opt.icon}`} aria-hidden="true" />
            </span>
            <span className="orch-mode-segment-copy">
              <span className="orch-mode-segment-label">{opt.label}</span>
            </span>
            {active && (
              <span className="orch-mode-segment-check" aria-hidden="true">
                <i className="fa-solid fa-check" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function OrchSelect({
  value,
  onChange,
  options,
  placeholder,
  loading = false,
  disabled = false,
  icon = "fa-list",
  emptyMessage = "No options available",
  searchable = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const showSearch = searchable && options.length > 5;
  const query = search.trim().toLowerCase();
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query))
    : options;

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    if (open && showSearch) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
    if (!open) setSearch("");
  }, [open, showSearch]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
  };

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setSearch("");
  };

  return (
    <div
      ref={containerRef}
      className={`orch-select${open ? " orch-select--open" : ""}${disabled ? " orch-select--disabled" : ""}`}
    >
      <button
        type="button"
        className="orch-select-trigger"
        onClick={handleToggle}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="orch-select-trigger-leading" aria-hidden="true">
          {loading ? (
            <i className="fa-solid fa-spinner fa-spin" />
          ) : (
            <i className={`fa-solid ${icon}`} />
          )}
        </span>
        <span className="orch-select-trigger-body">
          <span
            className={`orch-select-trigger-label${
              !selected && !loading ? " orch-select-trigger-label--placeholder" : ""
            }`}
          >
            {loading ? placeholder : selected?.label || placeholder}
          </span>
        </span>
        <i
          className={`fa-solid fa-chevron-down orch-select-chevron${open ? " orch-select-chevron--open" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && !disabled && !loading && (
        <div className="orch-select-dropdown" role="listbox">
          {showSearch && (
            <div className="orch-select-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                aria-label="Search options"
              />
            </div>
          )}
          <ul className="orch-select-list">
            {filtered.length === 0 ? (
              <li className="orch-select-empty">{emptyMessage}</li>
            ) : (
              filtered.map((opt) => {
                const active = value === opt.value;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`orch-select-option${active ? " orch-select-option--active" : ""}`}
                      onClick={() => handleSelect(opt.value)}
                    >
                      <span className="orch-select-option-label">{opt.label}</span>
                      {active && (
                        <i className="fa-solid fa-check orch-select-option-check" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function OrchestratorOnClickPanel({
  projectId,
  value,
  onChange,
  onSave,
  uiEventSaving = false,
}) {
  const [mode, setMode] = useState(
    SERVICE_MODE_ENABLED && value?.mode === "svc" ? "svc" : "orch"
  );
  const [workflowId, setWorkflowId] = useState(value?.workflowId || "");
  const [query, setQuery] = useState(value?.query || "");
  const [serviceAppId, setServiceAppId] = useState(value?.serviceAppId || "");
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [serviceApps, setServiceApps] = useState([]);
  const [loadingServiceApps, setLoadingServiceApps] = useState(false);
  const queryDebounceRef = useRef(null);

  useEffect(() => {
    if (!value) return;
    if (SERVICE_MODE_ENABLED && value.mode) setMode(value.mode);
    else setMode("orch");
    if (value.workflowId != null) setWorkflowId(String(value.workflowId));
    if (value.query != null) setQuery(String(value.query));
    if (value.serviceAppId != null) setServiceAppId(String(value.serviceAppId));
  }, [value?.mode, value?.workflowId, value?.workflowName, value?.query, value?.serviceAppId]);

  useEffect(() => () => {
    if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
  }, []);

  useEffect(() => {
    if (mode !== "orch" || !projectId) {
      setWorkflows([]);
      return;
    }

    let cancelled = false;

    const fetchWorkflows = async () => {
      setLoadingWorkflows(true);
      try {
        const res = await WorkflowService.getOrchestratorPublishedHistory(projectId, 0, 50);
        if (cancelled) return;

        const dtoPage =
          res?.orchestratorDTOS ||
          res?.orchestratorDTOPage ||
          res?.data?.orchestratorDTOS ||
          null;
        const content = dtoPage?.content || [];
        const normalized = content
          .map((item) => ({
            id: String(item.id),
            name: item.name || item.orchestratorName || item.title || `Orchestrator ${item.id}`,
          }))
          .filter((w) => w.id);

        setWorkflows(normalized);
      } catch (e) {
        console.error("Error fetching orchestrators:", e);
        if (!cancelled) setWorkflows([]);
      } finally {
        if (!cancelled) setLoadingWorkflows(false);
      }
    };

    fetchWorkflows();
    return () => { cancelled = true; };
  }, [mode, projectId]);

  useEffect(() => {
    if (!SERVICE_MODE_ENABLED) {
      setServiceApps([]);
      return;
    }

    let cancelled = false;

    const fetchServiceApps = async () => {
      setLoadingServiceApps(true);
      try {
        const appsresponse = await AgentService.getConnectedApps("OPEN_API");
        if (cancelled) return;

        const appsData = appsresponse?.thirdPartyAppsDTOPage?.content || [];
        const normalized = appsData
          .map((item) => ({
            id: String(item.id),
            name: item.name || item.credentialName || "Unnamed",
            provider: item.provider || "OPEN_API",
          }))
          .filter((a) => a.id);

        setServiceApps(normalized);
      } catch (e) {
        console.error("Error fetching service apps:", e);
        if (!cancelled) setServiceApps([]);
      } finally {
        if (!cancelled) setLoadingServiceApps(false);
      }
    };

    fetchServiceApps();
    return () => { cancelled = true; };
  }, []);

  const emitChange = (nextMode, nextWorkflowId, nextServiceAppId, nextQuery = query, nextWorkflowName = "") => {
    const config =
      nextMode === "orch"
        ? {
            executionType: "ORCHESTRATOR",
            workflowId: nextWorkflowId,
            workflowName: nextWorkflowName || "",
            query: String(nextQuery ?? ""),
            mode: "orch",
          }
        : { executionType: "SERVICE", service: { serviceAppId: nextServiceAppId }, mode: "svc" };

    onChange?.(config);
    onSave?.(config);
  };

  const handleModeChange = (nextMode) => {
    if (!SERVICE_MODE_ENABLED && nextMode !== "orch") return;
    setMode(nextMode);
    if (nextMode === "orch" && workflowId) {
      const name = workflows.find((w) => w.id === workflowId)?.name || value?.workflowName || "";
      emitChange(nextMode, workflowId, serviceAppId, query, name);
    } else if (nextMode === "svc" && serviceAppId) {
      emitChange(nextMode, workflowId, serviceAppId, query);
    }
  };

  const handleWorkflowChange = (nextWorkflowId) => {
    setWorkflowId(nextWorkflowId);
    if (nextWorkflowId) {
      const name = workflows.find((w) => w.id === nextWorkflowId)?.name || "";
      emitChange("orch", nextWorkflowId, serviceAppId, query, name);
    }
  };

  const handleClearOrchestrator = () => {
    if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
    setWorkflowId("");
    setQuery("");
    onChange?.({
      remove: true,
      clear: true,
      executionType: "ORCHESTRATOR",
      workflowId: "",
      workflowName: "",
      query: "",
      mode: "orch",
    });
  };

  const handleQueryChange = (nextQuery) => {
    setQuery(nextQuery);
    if (!workflowId) return;
    if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
    queryDebounceRef.current = setTimeout(() => {
      const name = workflows.find((w) => w.id === workflowId)?.name || value?.workflowName || "";
      emitChange("orch", workflowId, serviceAppId, nextQuery, name);
    }, 450);
  };

  const handleServiceAppChange = (nextServiceAppId) => {
    setServiceAppId(nextServiceAppId);
    if (nextServiceAppId) emitChange("svc", workflowId, nextServiceAppId, query);
  };

  const workflowOptions = (() => {
    const opts = workflows.map((w) => ({
      value: w.id,
      label: w.name,
    }));
    // Keep the previously selected workflow visible even if it is outside the fetched page.
    if (workflowId && !opts.some((o) => o.value === workflowId)) {
      opts.unshift({
        value: workflowId,
        label: value?.workflowName || `Orchestrator ${workflowId}`,
      });
    }
    return opts;
  })();

  const serviceAppOptions = serviceApps.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  const workflowPlaceholder = !projectId
    ? "Project not available"
    : loadingWorkflows
      ? "Loading workflows…"
      : "Choose a workflow";

  const servicePlaceholder = loadingServiceApps ? "Loading services…" : "Choose a connected app";

  return (
    <div className="properties-form orchestrator-onclick-form">
      <div className="property-group orch-property-group">
        <label className="property-label orch-section-label">
          <i className="fa-solid fa-bolt" aria-hidden="true" />
          Execution mode
        </label>
        <ModeSegment
          value={mode}
          onChange={handleModeChange}
          disabled={uiEventSaving}
        />
      </div>

      {mode === "orch" && (
        <>
          <div className="property-group orch-property-group">
            <div className="orch-service-header">
              <label className="property-label orch-section-label">
                <i className="fa-solid fa-diagram-project" aria-hidden="true" />
                Workflow
              </label>
              {workflowId ? (
                <button
                  type="button"
                  className="orch-onclick-clear-btn"
                  title="Remove orchestrator from this element"
                  disabled={uiEventSaving}
                  onClick={handleClearOrchestrator}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                  Clear
                </button>
              ) : null}
            </div>
            <OrchSelect
              value={workflowId}
              onChange={handleWorkflowChange}
              options={workflowOptions}
              placeholder={workflowPlaceholder}
              loading={loadingWorkflows}
              disabled={!projectId || uiEventSaving}
              icon="fa-diagram-project"
              searchable
              emptyMessage={
                !projectId
                  ? "Project not available"
                  : "No workflows found yet"
              }
            />
            {!loadingWorkflows && projectId && workflows.length === 0 && (
              <p className="orch-onclick-hint">No workflows found yet.</p>
            )}
          </div>

          <div className="property-group orch-property-group">
            <label className="property-label orch-section-label">
              <i className="fa-solid fa-message" aria-hidden="true" />
              Query
            </label>
            <input
              type="text"
              className="property-input property-input-text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onBlur={() => {
                if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
                if (workflowId) {
                  const name = workflows.find((w) => w.id === workflowId)?.name || value?.workflowName || "";
                  emitChange("orch", workflowId, serviceAppId, query, name);
                }
              }}
              placeholder="callOrchestrator(id, query)"
              disabled={uiEventSaving}
            />
          </div>

          <div className="orch-onclick-info">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>Injects callOrchestrator(orchestratorId, query) on the action target.</span>
          </div>
        </>
      )}

      {SERVICE_MODE_ENABLED && mode === "svc" && (
        <>
          <div className="property-group orch-property-group">
            <div className="orch-service-header">
              <label className="property-label orch-section-label">
                <i className="fa-solid fa-plug" aria-hidden="true" />
                Connected app
              </label>
              <button
                type="button"
                className="orch-service-add-btn"
                title="Add REST API agent app"
                onClick={() =>
                  window.open(
                    `${window.location.origin}/settings?section=agentApps&provider=open_api&action=configure`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <i className="fa-solid fa-plus" aria-hidden="true" />
              </button>
            </div>
            <OrchSelect
              value={serviceAppId}
              onChange={handleServiceAppChange}
              options={serviceAppOptions}
              placeholder={servicePlaceholder}
              loading={loadingServiceApps}
              disabled={uiEventSaving}
              icon="fa-plug"
              searchable
              emptyMessage="No REST API Agent Apps found"
            />
            {!loadingServiceApps && serviceApps.length === 0 && (
              <p className="orch-onclick-hint">
                No REST API Agent Apps found. Create one in Agent Apps → REST API.
              </p>
            )}
          </div>

          <div className="orch-onclick-info">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>Calls your REST API directly. Configure endpoints inside Agent Apps.</span>
          </div>
        </>
      )}
    </div>
  );
}
